import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '@/shared/api'

export type NumberType = 'twiml' | 'vapi' | 'sip_trunk' | 'unattached'

export type TwilioNumberConfig = {
  sipAddress: string | null
  campaignId: string | null
  fromNumber: string | null
}

export type TwilioNumber = {
  sid: string
  phoneNumber: string
  friendlyName: string
  voiceUrl: string | null
  type: NumberType
  config?: TwilioNumberConfig
}

export function useTwilioNumbers() {
  const api = useApiClient()

  return useQuery({
    queryKey: ['twilio-numbers'],
    queryFn: async () => {
      return api<TwilioNumber[]>('/twilio-numbers')
    },
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useAttachTwilioNumber() {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (sid: string) => {
      return api<{ sid: string; phoneNumber: string; type: 'twiml' }>(
        `/twilio-numbers/${sid}/attach`,
        { method: 'POST' }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['twilio-numbers'] })
    },
  })
}

export function useConfigureTwilioNumber() {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { sid: string; config: Partial<TwilioNumberConfig> }) => {
      return api<TwilioNumberConfig>(
        `/twilio-numbers/${params.sid}/configure`,
        {
          method: 'PUT',
          body: JSON.stringify(params.config),
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['twilio-numbers'] })
    },
  })
}